/**
 * GM Race Client-Feel Config — shared force-model constants + kill switches.
 *
 * Single source for the continuous-hazard force model shared by the SERVER
 * bot path (GmEventHazardEngine defaults) and the CLIENT local-player path
 * (ClientHazardSystem + MovementSystem ice friction). Per-hazard authored
 * values ride `race:hazardsList`, so only defaults + model constants live
 * here — the anti-drift move (CLIENT_FEEL_SPEC §1).
 *
 * Kill switches are read BY REFERENCE each tick (the mobBumpConfig
 * precedent), so a dev-override flip works live mid-session.
 *
 * Two-section file (defaults + dev override — see race-debug.config.ts).
 *
 * @see docs/design/gm/GM_RACE_CLIENT_FEEL_SPEC.md §1/§7
 */

export interface RaceFeelConfig {
  /** Client-local continuous-hazard physics (conveyor/fan/ice) — §2. */
  enableClientHazardPhysics: boolean;
  /** Client-side powerup stat application (speed/jump/slow) — §4. */
  enableClientPowerupStats: boolean;

  /** Conveyor default drift (px/s) when the hazard authors no velocityX. */
  conveyorVelocityXDefault: number;
  /** Fan default vertical target (px/s, negative = up) when unauthored. */
  fanImpulseYDefault: number;
  /** Ground-deceleration multiplier while on an ice_patch (lower = slippier). */
  iceFrictionMultiplier: number;
  /** Client fan model: vy approach acceleration (px/s²) — 2× gravity.default (1500). */
  fanApproachAccel: number;
}

export const RACE_FEEL_DEFAULTS: RaceFeelConfig = {
  /** Kill switches (§7) — read by reference each tick, mobBumpConfig-style. */
  enableClientHazardPhysics: true,
  enableClientPowerupStats: true,

  /** Force-model defaults — MUST match hazard authoring expectations. */
  conveyorVelocityXDefault: 100,   // px/s, was GmEventHazardEngine inline `?? 100`
  fanImpulseYDefault: -500,        // px/s target, was GmEventHazardEngine inline `?? -500`
  iceFrictionMultiplier: 0.1,      // was MovementSystem ICE_FRICTION_MULTIPLIER
  fanApproachAccel: 3000,
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to RACE_FEEL_DEFAULTS. Flip a kill switch here (or mutate RACE_FEEL live
 * from the console) to restore pre-client-feel behavior instantly.
 */
export const RACE_FEEL_DEV_OVERRIDE: Partial<RaceFeelConfig> = {};

export const RACE_FEEL: RaceFeelConfig = {
  ...RACE_FEEL_DEFAULTS,
  ...RACE_FEEL_DEV_OVERRIDE,
};
